import { supabase } from "@/integrations/supabase/client";

// Implicit Terms of Use + Privacy Policy consent.
//
// The auth surfaces show "By continuing, you agree…" copy; clicking Google or
// submitting the email/password form counts as acceptance. Because the Google
// path round-trips through an OAuth redirect (which unmounts the auth
// component), we can't reliably write the timestamp inline. Instead we stash a
// pending flag right before authenticating, then flush it to the profile once a
// session exists, see flushPendingTerms, called from useAuth on auth changes.

const PENDING_KEY = "msth_terms_pending_v1";

/** Call right before signUp / signInWithPassword / signInWithOAuth. */
export const markTermsPending = (): void => {
  try {
    localStorage.setItem(PENDING_KEY, "1");
  } catch {
    /* SSR / storage disabled, non-critical, acceptance copy is still shown */
  }
};

/**
 * If the user just authenticated through our UI (pending flag set), stamp
 * terms_accepted_at on their profile, only the first time (where it's still
 * null). Idempotent and safe to call on every auth state change: it no-ops when
 * the flag is absent.
 */
export const flushPendingTerms = async (userId: string): Promise<void> => {
  let pending = false;
  try {
    pending = localStorage.getItem(PENDING_KEY) === "1";
  } catch {
    return;
  }
  if (!pending) return;

  const { error } = await supabase
    .from("profiles")
    .update({ terms_accepted_at: new Date().toISOString() })
    .eq("user_id", userId)
    .is("terms_accepted_at", null);

  // Clear the flag only once the write lands, so a transient failure retries on
  // the next auth change instead of silently dropping the record.
  if (!error) {
    try {
      localStorage.removeItem(PENDING_KEY);
    } catch {
      /* ignore */
    }
  }
};
